/**
 * Resource Utilization Chart (Compact Row Bars)
 *
 * Four-section panel (WQ-128: expanded from 2):
 * 1. Platform Services — 12 services in 3 groups (Core, Infrastructure, Observability)
 * 2. Infrastructure Metrics — 8 resource utilization bars
 * 3. Operational Health — cert expiry, backup recency, error rate, latency
 * 4. Tenant Resources — per-tenant CPU/memory/network breakdown
 *
 * Data comes from containerStatus.resources and systemUptime.components.
 * WQ-020: Added Platform Services section + stale-while-revalidate guard.
 * WQ-128: Prometheus migration, expanded metrics, service sub-metrics.
 */

import React from 'react';

export interface ResourceUtilizationChartProps {
  resources: {
    avgCpuPercent: number;
    avgMemoryPercent: number;
    diskPercent: number;
    dbConnectionPercent: number;
    networkRxMbps: number;
    queueDepthPercent: number;
    apiThroughputRps: number;
    hostDiskPercent: number;
    hostSwapPercent: number;
    apiLatencyP95Ms: number;
    apiErrorRatePercent: number;
    containerCountRunning: number;
    containerCountTotal: number;
  } | null;
  components: Record<string, {
    uptime: number;
    status: string;
    group?: string;
    [key: string]: unknown;
  }> | null;
  isLoading: boolean;
}

interface ResourceRow {
  key: string;
  label: string;
  subtitle: string;
  value: number;
  warnThreshold: number;
  critThreshold: number;
  isPercent: boolean;
  suffix: string;
  maxValue?: number;
}

const SERVICE_LABELS: Record<string, string> = {
  api: 'API Server',
  database: 'PostgreSQL',
  cache: 'Redis Cache',
  queue: 'RabbitMQ',
  traefik: 'Traefik',
  keycloak: 'Keycloak',
  ollama: 'Ollama',
  prometheus: 'Prometheus',
  grafana: 'Grafana',
  loki: 'Loki',
  platform_ui: 'Platform UI',
};

const GROUP_LABELS: Record<string, string> = {
  core: 'Core',
  infrastructure: 'Infrastructure',
  observability: 'Observability',
};

const GROUP_ORDER = ['core', 'infrastructure', 'observability'];

function getBarColor(value: number, warn: number, crit: number): string {
  if (value >= crit) return 'var(--danger, #ef4444)';
  if (value >= warn) return 'var(--warning, #f59e0b)';
  return 'var(--success, #22c55e)';
}

function getStatusDot(value: number, warn: number, crit: number): string {
  if (value >= crit) return '#ef4444';
  if (value >= warn) return '#f59e0b';
  return '#22c55e';
}

function getServiceDotColor(status: string): string {
  if (status === 'up') return '#22c55e';
  if (status === 'degraded') return '#f59e0b';
  return '#ef4444';
}

function getServiceStatusLabel(status: string): string {
  if (status === 'up') return 'Up';
  if (status === 'degraded') return 'Degraded';
  return 'Down';
}

/** Format a service sub-metric as a compact string. */
function formatSubMetric(svc: Record<string, unknown>): string {
  const parts: string[] = [];
  if (svc.response_time_ms != null) parts.push(`${svc.response_time_ms}ms p50`);
  if (svc.request_rate != null) parts.push(`${svc.request_rate} rps`);
  if (svc.pool_percent != null) parts.push(`${svc.active_connections}/${svc.max_connections} conns`);
  if (svc.db_size_mb != null) parts.push(`${svc.db_size_mb} MB`);
  if (svc.hit_rate != null) parts.push(`${svc.hit_rate}% hit`);
  if (svc.memory_used_mb != null) parts.push(`${svc.memory_used_mb} MB`);
  if (svc.total_messages != null) parts.push(`${svc.total_messages} queued`);
  if (svc.consumers != null) parts.push(`${svc.consumers} consumers`);
  if (svc.http_routers != null) parts.push(`${svc.http_routers} routers`);
  if (svc.model_count != null) parts.push(`${svc.model_count} models`);
  if (svc.targets_up != null) parts.push(`${svc.targets_up}/${svc.targets_total} targets`);
  return parts.join('  ');
}

export const ResourceUtilizationChart: React.FC<ResourceUtilizationChartProps> = ({
  resources,
  components,
  isLoading,
}) => {
  if (isLoading && !resources && !components) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', padding: 'var(--space-6, 24px) 0' }}>
        Loading...
      </div>
    );
  }

  const r = resources ?? {
    avgCpuPercent: 0, avgMemoryPercent: 0, diskPercent: 0,
    dbConnectionPercent: 0, networkRxMbps: 0, queueDepthPercent: 0,
    apiThroughputRps: 0, hostDiskPercent: 0, hostSwapPercent: 0,
    apiLatencyP95Ms: 0, apiErrorRatePercent: 0,
    containerCountRunning: 0, containerCountTotal: 0,
  };

  const allZero = r.avgCpuPercent === 0 && r.avgMemoryPercent === 0 && r.diskPercent === 0
    && r.dbConnectionPercent === 0 && r.networkRxMbps === 0 && r.queueDepthPercent === 0
    && r.apiThroughputRps === 0;
  const hasComponents = components && Object.keys(components).length > 0;

  // Nothing to show at all
  if (allZero && !hasComponents) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 'var(--space-2, 8px)', padding: 'var(--space-6, 24px) 0' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
        </svg>
        <span style={{ fontSize: 'var(--text-sm, 14px)' }}>No resource data available</span>
        <span style={{ fontSize: 'var(--text-xs, 12px)' }}>Metrics appear when containers are running</span>
      </div>
    );
  }

  // Group services by their group
  const groupedServices: Record<string, [string, typeof components extends null ? never : NonNullable<typeof components>[string]][]> = {};
  if (hasComponents) {
    for (const [key, svc] of Object.entries(components)) {
      const group = (svc.group as string) || 'core';
      if (!groupedServices[group]) groupedServices[group] = [];
      groupedServices[group].push([key, svc]);
    }
  }

  const infraRows: ResourceRow[] = [
    { key: 'cpu', label: 'CPU', subtitle: 'Container avg', value: r.avgCpuPercent, warnThreshold: 70, critThreshold: 85, isPercent: true, suffix: '%' },
    { key: 'memory', label: 'Memory', subtitle: 'Container avg', value: r.avgMemoryPercent, warnThreshold: 75, critThreshold: 90, isPercent: true, suffix: '%' },
    { key: 'hostDisk', label: 'Host Disk', subtitle: 'Root volume', value: r.hostDiskPercent, warnThreshold: 70, critThreshold: 90, isPercent: true, suffix: '%' },
    { key: 'disk', label: 'DB Disk', subtitle: 'Database size', value: r.diskPercent, warnThreshold: 80, critThreshold: 92, isPercent: true, suffix: '%' },
    { key: 'db', label: 'DB Conns', subtitle: 'Active / max', value: r.dbConnectionPercent, warnThreshold: 70, critThreshold: 85, isPercent: true, suffix: '%' },
    { key: 'network', label: 'Network (rx)', subtitle: 'Throughput', value: r.networkRxMbps, warnThreshold: 500, critThreshold: 900, isPercent: false, suffix: ' Mbps', maxValue: 1000 },
    { key: 'queue', label: 'Queue', subtitle: 'Message depth', value: r.queueDepthPercent, warnThreshold: 50, critThreshold: 75, isPercent: true, suffix: '%' },
    { key: 'swap', label: 'Swap', subtitle: 'Host swap', value: r.hostSwapPercent, warnThreshold: 1, critThreshold: 20, isPercent: true, suffix: '%' },
  ];

  // Operational health indicators (number + color, no bar)
  const opHealthItems = [
    {
      key: 'latency',
      label: 'API Latency p95',
      value: r.apiLatencyP95Ms,
      suffix: 'ms',
      color: r.apiLatencyP95Ms > 500 ? '#ef4444' : r.apiLatencyP95Ms > 100 ? '#f59e0b' : '#22c55e',
    },
    {
      key: 'errorRate',
      label: 'Error Rate (5xx)',
      value: r.apiErrorRatePercent,
      suffix: '%',
      color: r.apiErrorRatePercent > 5 ? '#ef4444' : r.apiErrorRatePercent > 1 ? '#f59e0b' : '#22c55e',
    },
    {
      key: 'containers',
      label: 'Containers',
      value: r.containerCountRunning,
      suffix: `/${r.containerCountTotal} run`,
      color: r.containerCountRunning < r.containerCountTotal * 0.5
        ? '#ef4444'
        : r.containerCountRunning < r.containerCountTotal
          ? '#f59e0b'
          : '#22c55e',
    },
    {
      key: 'apiLoad',
      label: 'API Load',
      value: r.apiThroughputRps,
      suffix: ' rps',
      color: 'var(--text-primary)',
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)', padding: 'var(--space-2, 8px) 0' }}>
      {/* Section 1: Platform Services — grouped */}
      {hasComponents && (
        <>
          <div style={{ fontSize: 'var(--text-xs, 11px)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Platform Services
          </div>
          {GROUP_ORDER.map((group) => {
            const entries = groupedServices[group];
            if (!entries || entries.length === 0) return null;
            return (
              <div key={group}>
                <div style={{ fontSize: 'var(--text-xs, 10px)', color: 'var(--text-muted)', marginBottom: 2, opacity: 0.7 }}>
                  {GROUP_LABELS[group] || group}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1, 2px)' }}>
                  {entries.map(([key, svc]) => {
                    const subMetric = formatSubMetric(svc as Record<string, unknown>);
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)', height: 28 }}>
                        <div style={{
                          width: 7, height: 7, borderRadius: '50%',
                          backgroundColor: getServiceDotColor(svc.status), flexShrink: 0,
                        }} />
                        <div style={{ width: 90, fontSize: 'var(--text-xs, 12px)', fontWeight: 500, color: 'var(--text-primary)', flexShrink: 0 }}>
                          {SERVICE_LABELS[key] || key}
                        </div>
                        <div style={{
                          fontSize: 'var(--text-xs, 10px)',
                          color: getServiceDotColor(svc.status),
                          fontWeight: 600, width: 46, flexShrink: 0,
                        }}>
                          {getServiceStatusLabel(svc.status)}
                        </div>
                        {subMetric && (
                          <div style={{
                            flex: 1, fontSize: 'var(--text-xs, 10px)',
                            color: 'var(--text-muted)', fontFamily: 'monospace',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {subMetric}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div style={{ height: 1, background: 'var(--border-primary, rgba(255,255,255,0.08))', margin: 'var(--space-1, 4px) 0' }} />
        </>
      )}

      {/* Section 2: Infrastructure Metrics */}
      {!allZero ? (
        <>
          <div style={{ fontSize: 'var(--text-xs, 11px)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Infrastructure Metrics
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2, 8px)' }}>
            {infraRows.map((row) => (
              <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3, 12px)', height: 32 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: getStatusDot(row.value, row.warnThreshold, row.critThreshold),
                  flexShrink: 0,
                }} />
                <div style={{ width: 76, flexShrink: 0 }}>
                  <div style={{ fontSize: 'var(--text-xs, 12px)', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                    {row.label}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs, 10px)', color: 'var(--text-muted)', lineHeight: 1.2 }}>
                    {row.subtitle}
                  </div>
                </div>
                {row.isPercent ? (
                  <>
                    <div style={{ flex: 1, position: 'relative', height: 12, background: 'var(--bg-secondary, #1a1a2e)', borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.min(row.value, 100)}%`,
                          height: '100%',
                          background: getBarColor(row.value, row.warnThreshold, row.critThreshold),
                          borderRadius: 3,
                          transition: 'width 0.5s ease',
                          minWidth: row.value > 0 ? 2 : 0,
                        }}
                      />
                      <div style={{ position: 'absolute', left: `${row.warnThreshold}%`, top: 0, width: 1, height: '100%', backgroundColor: 'rgba(245, 158, 11, 0.4)' }} />
                      <div style={{ position: 'absolute', left: `${row.critThreshold}%`, top: 0, width: 1, height: '100%', backgroundColor: 'rgba(239, 68, 68, 0.4)' }} />
                    </div>
                    <div style={{
                      width: 48, textAlign: 'right', fontSize: 'var(--text-xs, 12px)',
                      fontFamily: 'monospace', fontWeight: 700,
                      color: getBarColor(row.value, row.warnThreshold, row.critThreshold),
                      flexShrink: 0,
                    }}>
                      {row.value}{row.suffix}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1, position: 'relative', height: 12, background: 'var(--bg-secondary, #1a1a2e)', borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.min((row.value / (row.maxValue || 100)) * 100, 100)}%`,
                          height: '100%',
                          background: getBarColor(row.value, row.warnThreshold, row.critThreshold),
                          borderRadius: 3,
                          transition: 'width 0.5s ease',
                          minWidth: row.value > 0 ? 2 : 0,
                        }}
                      />
                      <div style={{ position: 'absolute', left: `${((row.warnThreshold) / (row.maxValue || 100)) * 100}%`, top: 0, width: 1, height: '100%', backgroundColor: 'rgba(245, 158, 11, 0.4)' }} />
                      <div style={{ position: 'absolute', left: `${((row.critThreshold) / (row.maxValue || 100)) * 100}%`, top: 0, width: 1, height: '100%', backgroundColor: 'rgba(239, 68, 68, 0.4)' }} />
                    </div>
                    <div style={{
                      width: 72, textAlign: 'right', fontSize: 'var(--text-xs, 12px)',
                      fontFamily: 'monospace', fontWeight: 700,
                      color: getBarColor(row.value, row.warnThreshold, row.critThreshold),
                      flexShrink: 0,
                    }}>
                      {row.value}{row.suffix}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Section 3: Operational Health */}
          <div style={{ height: 1, background: 'var(--border-primary, rgba(255,255,255,0.08))', margin: 'var(--space-1, 4px) 0' }} />
          <div style={{ fontSize: 'var(--text-xs, 11px)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Operational Health
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3, 12px)' }}>
            {opHealthItems.map((item) => (
              <div key={item.key} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                minWidth: 80, padding: '4px 8px',
                background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                borderRadius: 6,
              }}>
                <div style={{ fontSize: 'var(--text-xs, 10px)', color: 'var(--text-muted)', marginBottom: 2 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 'var(--text-sm, 14px)', fontFamily: 'monospace', fontWeight: 700, color: item.color }}>
                  {item.value}{item.suffix}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm, 13px)', padding: 'var(--space-4, 16px) 0' }}>
          No container metrics available
        </div>
      )}
    </div>
  );
};

export default ResourceUtilizationChart;
